import { Pagination, Permission, Role } from '@ekkogmbh/apisdk';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DataTable, dataTableHighlight } from 'src/Common/Components/DataTable';
import ScrollableStack from 'src/Common/Components/ScrollableStack';
import { GenericDialog } from '../../Common/Components/GenericDialog';
import Permissions from 'src/Common/Permissions';
import { RolePanel } from './RolePanel';
import { ContentControl } from 'src/Common/Components/ContentControl';
import { ContentControlButton } from 'src/Common/Components/ContentControl/ContentControlButton';
import { Add, Delete, Edit, Group } from '@mui/icons-material';
import { RolePermissionSelect } from './RolePermissionSelect';
import useEkko from 'src/Common/Providers/EkkoProvider';
import useRequest from 'src/Common/Hooks/useRequest';
import { useContentControl } from 'src/Common/Stores/ContentControlProvider';
import { Box, Divider, Skeleton, Stack, Typography } from '@mui/material';
import TooltipHardened from 'src/Common/Components/TooltipHardened';
import useNavigation from 'src/Common/Providers/NavigationProvider';
import { useNavigate } from 'react-router';
import usePagination from 'src/Common/Providers/PaginationProvider';

const ROW_MIN_HEIGHT = 64;

export const RoleManagementContent = (): React.JSX.Element => {
  const { api, hasPermission } = useEkko();
  const { refresh } = usePagination();
  const { setPanelOpen, togglePanel } = useContentControl();
  const { scrollTop } = useNavigation();
  const navigate = useNavigate();

  const [selectedRole, setSelectedRole] = useState<Role>();
  const [isDialogOpen, setDialogOpen] = useState<boolean>(false);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [isFetchingPerms, startFetchingPerms] = useRequest<Permission[]>();
  const [, startSaving] = useRequest<void>();

  const dismiss = useCallback(() => {
    setDialogOpen(false);
    setPanelOpen(false);
    setSelectedRole(undefined);
  }, []);

  const fetchPermissions = useCallback(() => {
    startFetchingPerms(
      async () => await api.getPermissions(),
      (result) => setPermissions(result ?? []),
    );
  }, []);

  useEffect(() => {
    fetchPermissions();
  }, []);

  const fetchItems = useCallback(async (pagination: Pagination) => await api.getRoles(pagination), []);

  const saveRole = useCallback(
    (payload: Role | Partial<Role>) => {
      startSaving(
        async () => {
          if (payload?.id === undefined && selectedRole === undefined) {
            await api.addRole(payload as Role);
          } else {
            await api.updateRole(payload as Role);
          }
        },
        () => {
          dismiss();
          refresh();
          scrollTop();
        },
      );
    },
    [selectedRole],
  );

  const deleteRole = useCallback(() => {
    if (selectedRole === undefined) {
      return;
    }
    startSaving(async () => await api.deleteRole(selectedRole), refresh);
  }, [selectedRole]);

  const panel = useMemo(
    () => <RolePanel role={selectedRole} permissions={permissions} closeHandler={dismiss} saveHandler={saveRole} />,
    [selectedRole, permissions],
  );

  const deleteDialogText = selectedRole ? `Delete Role ${selectedRole.name}?` : '';

  return (
    <ContentControl
      dialog={
        <GenericDialog
          type="confirmation"
          maxWidth={'sm'}
          fullWidth={true}
          centered={true}
          open={isDialogOpen}
          title={'Delete Role'}
          text={deleteDialogText}
          onClose={dismiss}
          onConfirm={deleteRole}
        />
      }
      panel={panel}
      buttons={[
        <ContentControlButton
          key={'add'}
          content={<Add />}
          onClick={() => {
            setSelectedRole(undefined);
            togglePanel();
          }}
          tooltip={'Add Role'}
          requiredPermission={Permissions.ROLES_WRITE}
        />,
      ]}
    >
      <DataTable
        fetchItems={fetchItems}
        maxExpandedActions={3}
        actions={[
          {
            title: 'Users',
            icon: <Group />,
            onClick: (entry: Role) => {
              navigate('/mappings/role/' + entry.id);
            },
            disabled: !hasPermission(Permissions.USERS_READ),
          },
          {
            title: 'Edit',
            icon: <Edit />,
            onClick: (entry: Role) => {
              dismiss();
              setSelectedRole(entry);
              setPanelOpen(true);
              scrollTop();
            },
            disabled: !hasPermission(Permissions.ROLES_WRITE),
          },
          {
            title: 'Delete',
            icon: <Delete />,
            onClick: (entry: Role) => {
              dismiss();
              setSelectedRole(entry);
              setDialogOpen(true);
            },
            disabled: !hasPermission(Permissions.ROLES_WRITE),
          },
        ]}
        columns={[
          {
            header: 'Name',
            body: (entry: Role, searchText?: string) => (
              <ScrollableStack direction="row" width="100%" minHeight={ROW_MIN_HEIGHT} alignItems="center">
                {dataTableHighlight(entry.name, [searchText ?? ''])}
              </ScrollableStack>
            ),
            filterField: 'name',
            sortField: 'R.name',
            proportionalContent: '20%',
          },
          {
            header: 'Coordinate',
            body: (entry: Role, searchText?: string) => (
              <ScrollableStack direction="row" width="100%" minHeight={ROW_MIN_HEIGHT} alignItems="center">
                {dataTableHighlight(entry.coordinate, [searchText ?? ''])}
              </ScrollableStack>
            ),
            filterField: 'coordinate',
            sortField: 'Node.materializedPath',
            proportionalContent: '30%',
          },
          {
            header: 'Permissions',
            body: (entry: Role) => {
              const rolePerms = entry.permissions ?? [];

              const tooltipContent =
                rolePerms.length > 0 ? (
                  <Stack spacing={1.5} sx={{ p: 1.5, minWidth: 220 }}>
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      textTransform="uppercase"
                      color="text.secondary"
                      letterSpacing={0.5}
                    >
                      Permissions ({rolePerms.length})
                    </Typography>
                    <Divider />
                    <Stack spacing={0.25}>
                      {[...rolePerms].sort().map((perm) => (
                        <Typography key={perm} variant="body2" fontFamily="monospace">
                          {perm}
                        </Typography>
                      ))}
                    </Stack>
                  </Stack>
                ) : undefined;

              return (
                <ScrollableStack direction="row" width="100%" minHeight={ROW_MIN_HEIGHT} alignItems="center">
                  {isFetchingPerms ? (
                    <Skeleton width={'100%'} height={10} />
                  ) : (
                    <TooltipHardened arrow tooltip={tooltipContent}>
                      <Box width="100%">
                        <RolePermissionSelect
                          variant={'standard'}
                          disableUnderline
                          role={entry}
                          allPermissions={permissions}
                          saveHandler={(role) => {
                            if (!hasPermission(Permissions.ROLES_WRITE)) {
                              enqueueSnackbar('Missing write permission.', { variant: 'error' });
                              refresh();
                              return;
                            }
                            saveRole(role);
                          }}
                          noDialog={!hasPermission(Permissions.ROLES_WRITE)}
                        />
                      </Box>
                    </TooltipHardened>
                  )}
                </ScrollableStack>
              );
            },
            proportionalContent: '50%',
          },
        ]}
      />
    </ContentControl>
  );
};
